import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { featuresActions } from '../../../store';
import { SURFACE_BUTTONS_CONFIG } from '../../../store/slices/featuresSlice';
import styles from './SurfaceButtons.module.css';

/**
 * SurfaceButtons - Displays inline clickable surface buttons for exterior/kitchen tabs
 * When clicked, sets the activeSeriesId in the features store for ProductList to use
 * Maintains the last clicked button in the features store
 * Defaults to the first button initially
 * 
 * @param {string} tab - The current tab ('exterior' or 'kitchen')
 * @param {Array} surfaces - Optional custom surfaces array, defaults to config data
 * @param {Function} onSurfaceClick - Optional callback when a surface button is clicked
 */
export default function SurfaceButtons({ tab, surfaces, onSurfaceClick }) {
    const dispatch = useDispatch();
    const selectedSurface = useSelector(state => state.features.selectedSurfaces[tab]);
    const lastClickedSurfaceButton = useSelector(state => state.features.lastClickedSurfaceButton);
    const paintPackagesActive = useSelector(state => state.features.paintPackagesActive);
    const sceneSurfaces = useSelector(state => state.features.sceneSurfaces);
    
    // Surfaces that only show when detected in the scene
    const conditionalSurfaces = ['Stone/Brick'];
    
    // Use provided surfaces or fall back to config data, filtering conditional ones
    const allSurfaces = surfaces || SURFACE_BUTTONS_CONFIG[tab] || [];
    const surfaceList = allSurfaces.filter(surface => 
        !conditionalSurfaces.includes(surface.id) || sceneSurfaces.includes(surface.id)
    );

    // For kitchen tab only, default to first button (Floor) if no selection exists
    useEffect(() => {
        if (tab === 'kitchen' && surfaceList.length > 0 && !selectedSurface) {
            dispatch(featuresActions.setSelectedSurfaceForTab({
                tab,
                surfaceId: surfaceList[0].id
            }));
        }
    }, [tab, surfaceList, selectedSurface, dispatch]);



    const handleSurfaceClick = (surface) => {
        dispatch(featuresActions.setSelectedSurfaceForTab({
            tab,
            surfaceId: surface.id
        }));
        
        // Call optional callback
        if (onSurfaceClick) {
            onSurfaceClick(surface);
        }
    };

    if (surfaceList.length === 0) {
        return null;
    }


    return (
        <div className={styles.surfaceButtonsContainer}>
            {surfaceList.map((surface) => (
                <button
                    key={surface.id}
                    className={`${styles.surfaceButton} ${selectedSurface === surface.id ? styles.selected : ''}`}
                    onClick={() => handleSurfaceClick(surface)}
                    aria-pressed={selectedSurface === surface.id}
                >
                    {surface.label}
                </button>
            ))}
        </div>
    );
}
